import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronLeft, FileText, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { useAuth } from "@/context/AuthContext";
import { downloadFile, request } from "@/lib/api";
import { loadRazorpayCheckout } from "@/lib/razorpay";
import { appointmentSlots, formatBookingDate, getLabCollectionDateOptions } from "@/lib/appointments";
import { CART_STORAGE_KEY, money, readStoredCart } from "@/pages/LabTestsPage";

const RELATIONSHIPS = ["Self", "Spouse", "Parent", "Child", "Child (18+)", "Other dependent"];

export default function LabCheckoutPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const collectionDates = getLabCollectionDateOptions();
  const [cartIds] = useState(readStoredCart);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    patient_name: "",
    patient_gender: "",
    patient_age: "",
    relationship: "Self",
    contact_mobile: "",
    address: "",
    city: "",
    pincode: "",
    collection_date: collectionDates[0] || "",
    collection_slot: appointmentSlots[0],
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!cartIds.length) return;
    request("/lab-tests/cart/price", { method: "POST", body: JSON.stringify(cartIds) }, token)
      .then(setDetail)
      .catch(() => setError("We couldn't price your cart. Please go back and try again."));
  }, [cartIds, token]);

  useEffect(() => {
    // Prefill from the employee record — the person ordering is usually the
    // person being tested.
    request("/portal/profile", {}, token)
      .then((profile) =>
        setForm((current) => ({
          ...current,
          patient_name: current.patient_name || profile.name || "",
          patient_gender: current.patient_gender || profile.gender || "",
          contact_mobile: current.contact_mobile || profile.mobile || "",
        })),
      )
      .catch(() => {});
  }, [token]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const ready =
    detail?.items?.length &&
    form.patient_name.trim().length > 1 &&
    form.patient_gender &&
    Number(form.patient_age) >= 1 &&
    /^[6-9]\d{9}$/.test(form.contact_mobile) &&
    form.address.trim().length >= 8 &&
    form.city.trim().length >= 2 &&
    /^\d{6}$/.test(form.pincode) &&
    form.collection_date &&
    form.collection_slot;

  async function placeAndPay() {
    setBusy(true);
    setError("");
    try {
      const order = await request(
        "/lab-tests/orders",
        {
          method: "POST",
          body: JSON.stringify({
            ...Object.fromEntries(Object.entries(form).filter(([key]) => key !== "place_id")),
            patient_age: Number(form.patient_age),
            test_ids: cartIds,
          }),
        },
        token,
      );
      const checkoutOrder = await request(`/lab-tests/orders/${order.order_id}/payment`, { method: "POST" }, token);
      const scriptLoaded = await loadRazorpayCheckout();
      if (!scriptLoaded || !window.Razorpay) {
        throw new Error("Secure payment could not be opened. Please try again.");
      }
      const checkout = new window.Razorpay({
        key: checkoutOrder.key_id,
        amount: checkoutOrder.amount,
        currency: checkoutOrder.currency,
        name: checkoutOrder.name,
        description: checkoutOrder.description,
        order_id: checkoutOrder.order_id,
        prefill: checkoutOrder.prefill,
        theme: { color: "#166534" },
        handler: async (response) => {
          try {
            const paid = await request(
              `/lab-tests/orders/${order.order_id}/payment/verify`,
              { method: "POST", body: JSON.stringify(response) },
              token,
            );
            // The cart has become an order; leaving it filled would invite a
            // second, accidental purchase of the same tests.
            try {
              window.localStorage.removeItem(CART_STORAGE_KEY);
            } catch {
              /* nothing depends on this succeeding */
            }
            setConfirmed(paid);
          } catch (verifyError) {
            setError(verifyError.message);
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      checkout.open();
    } catch (placeError) {
      setError(placeError.message);
      setBusy(false);
    }
  }

  if (confirmed) {
    return (
      <section className="page-stack" data-testid="lab-order-confirmed">
        <div className="complimentary-confirmation">
          <CheckCircle2 size={23} />
          <div>
            <p className="eyebrow">Order confirmed</p>
            <h2>{confirmed.items.length} test{confirmed.items.length === 1 ? "" : "s"} for {confirmed.patient.name}</h2>
            <p>{formatBookingDate(confirmed.collection_date)} · {confirmed.collection_slot}</p>
            <p>{confirmed.address}, {confirmed.city} {confirmed.pincode}</p>
            <p data-testid="lab-order-reference">Reference {confirmed.order_id} · paid {money(confirmed.amount)}</p>
            <p>Your collection letter is in your email and here in the portal. Our team will call before arriving.</p>
            <Button
              variant="outline"
              onClick={() => downloadFile(`/lab-tests/orders/${confirmed.order_id}/letter`, `collection-letter-${confirmed.order_id}.pdf`, token)}
              data-testid="lab-order-letter-button"
            >
              <FileText size={17} />Download collection letter
            </Button>
          </div>
        </div>
        <Button variant="ghost" onClick={() => navigate("/offers")} data-testid="lab-back-to-offers">Back to offers</Button>
      </section>
    );
  }

  if (!cartIds.length) {
    return (
      <section className="page-stack" data-testid="lab-checkout-empty">
        <div className="empty-updates">
          <p>Your cart is empty.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/lab-tests")}><ChevronLeft size={16} />Browse tests</Button>
      </section>
    );
  }

  return (
    <section className="page-stack page-form" data-testid="lab-checkout-page">
      <div className="page-heading">
        <p className="eyebrow">Collection details</p>
        <h1>Where and when should we collect?</h1>
      </div>

      {error && <p className="form-error" role="alert" data-testid="lab-checkout-error">{error}</p>}

      <div className="form-card">
        <p className="eyebrow">Who are these tests for?</p>
        <label htmlFor="lab-patient-name">Full name</label>
        <Input id="lab-patient-name" value={form.patient_name} onChange={(event) => update("patient_name", event.target.value)} data-testid="lab-name-input" />

        <label htmlFor="lab-relationship">Relationship to you</label>
        <select id="lab-relationship" value={form.relationship} onChange={(event) => update("relationship", event.target.value)} data-testid="lab-relationship-select">
          {RELATIONSHIPS.map((item) => <option key={item}>{item}</option>)}
        </select>

        <div className="address-fields-row">
          <div>
            <label htmlFor="lab-gender">Gender</label>
            <select id="lab-gender" value={form.patient_gender} onChange={(event) => update("patient_gender", event.target.value)} data-testid="lab-gender-select">
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label htmlFor="lab-age">Age</label>
            <Input id="lab-age" inputMode="numeric" maxLength="3" value={form.patient_age} onChange={(event) => update("patient_age", event.target.value.replace(/\D/g, ""))} data-testid="lab-age-input" />
          </div>
        </div>

        <label htmlFor="lab-mobile">Mobile number for the phlebotomist</label>
        <Input id="lab-mobile" inputMode="tel" maxLength="10" value={form.contact_mobile} onChange={(event) => update("contact_mobile", event.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile number" data-testid="lab-mobile-input" />

      </div>

      <div className="form-card">
        <p className="eyebrow">Collection address</p>
        {/* The component calls onAddressSelect on every keystroke as well as on
            a pick, so it has to be a real function even when we only want the
            place id. */}
        <AddressAutocomplete
          id="lab-address"
          token={token}
          value={form.address}
          onChange={(value) => update("address", value)}
          onAddressSelect={(place) =>
            setForm((current) => ({
              ...current,
              place_id: place?.placeId || null,
              // Filled from the chosen address rather than asked for twice.
              // Anything the lookup didn't return is left as typed.
              city: place?.city || current.city,
              pincode: place?.postcode || current.pincode,
            }))
          }
          placeholder="Search or enter the area"
          testId="lab-address-input"
        />
        <div className="address-fields-row">
          <div>
            <label htmlFor="lab-city">City</label>
            <Input id="lab-city" value={form.city} onChange={(event) => update("city", event.target.value)} data-testid="lab-city-input" />
          </div>
          <div>
            <label htmlFor="lab-pincode">PIN code</label>
            <Input id="lab-pincode" inputMode="numeric" maxLength="6" value={form.pincode} onChange={(event) => update("pincode", event.target.value.replace(/\D/g, ""))} data-testid="lab-pincode-input" />
          </div>
        </div>

      </div>

      <div className="form-card">
        <p className="eyebrow">When should they collect?</p>
        <p className="step-hint" data-testid="lab-collection-hint">Collections run every day, including Saturday and Sunday.</p>
        <label htmlFor="lab-date">Collection date</label>
        <select id="lab-date" value={form.collection_date} onChange={(event) => update("collection_date", event.target.value)} data-testid="lab-date-select">
          {collectionDates.map((day) => <option value={day} key={day}>{formatBookingDate(day)}</option>)}
        </select>
        <label htmlFor="lab-slot">Collection slot</label>
        <select id="lab-slot" value={form.collection_slot} onChange={(event) => update("collection_slot", event.target.value)} data-testid="lab-slot-select">
          {appointmentSlots.map((slot) => <option key={slot}>{slot}</option>)}
        </select>
      </div>

      {detail && (
        <div className="lab-cart-total" data-testid="lab-checkout-summary">
          <div><span>{detail.items.length} test{detail.items.length === 1 ? "" : "s"}</span><s>{money(detail.mrp_total)}</s></div>
          <div><span>Your price</span><strong>{money(detail.tests_total)}</strong></div>
          {/* The saving is against the tests alone — the visit fee is a charge,
              not a discount lost — so it is shown as its own line. */}
          <div><span>You save</span><strong className="lab-saving">{money(detail.mrp_total - detail.tests_total)}</strong></div>
          <div data-testid="lab-collection-fee"><span>Home visit charge</span><strong>{money(detail.collection_fee)}</strong></div>
          <div className="lab-cart-total__due"><span>To pay</span><strong>{money(detail.amount)}</strong></div>
        </div>
      )}

      <Button type="button" disabled={!ready || busy} onClick={placeAndPay} data-testid="lab-pay-button">
        <ShieldCheck size={17} />{busy ? "Opening secure payment…" : `Pay ${detail ? money(detail.amount) : ""} securely`}
      </Button>
      <Button type="button" variant="ghost" onClick={() => navigate("/lab-tests")} data-testid="lab-back-button">
        <ChevronLeft size={16} />Back to tests
      </Button>
    </section>
  );
}
